import { defineStore } from 'pinia';
import { ref, computed, useRequestHeaders } from '#imports';
import { apiGetLoggedInUserInfo, UserDto } from '~/api/auth';
import { FetchError } from 'ofetch';
import { getErrorStatus } from '~/api';
import { AuthCredential } from 'firebase/auth';
import { useFirebase } from '~/composables/use-firebase';
import { usePostHog } from '~/composables/usePostHog';
import { identifyUser } from '~/utils/posthog';
import { useAccountStore } from './account';

export const useAuthUserStore = defineStore('auth-user', () => {
  const authUser = ref<UserDto | null>(null);
  const isLoggedIn = computed<boolean>(() => authUser.value !== null);
  const credentialToLinkOnLogin = ref<null | AuthCredential>(null);

  const setCredentialToLinkOnLogin = (data: AuthCredential | null): void => {
    credentialToLinkOnLogin.value = data;
  };

  const initials = computed<string>(() => {
    if (authUser.value === null) {
      return '';
    }
    const [firstName, lastName] = authUser.value.fullName.split(' ');

    if (!lastName) {
      return firstName[0];
    }
    return `${firstName[0]} ${lastName[0]}`;
  });

  const setUser = (user: UserDto) => {
    authUser.value = user;
  };

  const initializeAuthUser = async (): Promise<void> => {
    if (authUser.value) {
      return;
    }

    if (
      import.meta.server &&
      !useRequestHeaders(['authorization']).authorization
    ) {
      return;
    }

    try {
      authUser.value = await apiGetLoggedInUserInfo();
      // Identify user in PostHog when session is initialized
      if (authUser.value) {
        identifyUser(authUser.value);
      }
    } catch (err) {
      // 401 (expired/invalid token) and 403 are treated as logged-out rather
      // than fatal, so SSR degrades gracefully and the client can recover.
      const status = getErrorStatus(err);
      if (status !== 401 && status !== 403) {
        throw err;
      }
    }
  };

  const clearSession = (): Promise<void> => {
    // Track logout event before clearing user data
    if (authUser.value) {
      const { capture, reset } = usePostHog();
      capture('user_logged_out', {
        user_id: authUser.value.id,
        email: authUser.value.email,
      });

      // Reset PostHog user identity to anonymous
      reset();
    }

    // Sign out of Firebase so the auth service worker stops injecting an ID
    // token — otherwise the user would be silently logged back in on the next
    // navigation. Client-only; callers that reload the app must await it.
    let signOut: Promise<void> = Promise.resolve();
    if (import.meta.client) {
      const { auth } = useFirebase();
      signOut = auth.signOut().catch(() => {
        // Ignore: session is already being cleared locally.
      });
    }

    authUser.value = null;
    setCredentialToLinkOnLogin(null);
    useAccountStore().clearCurrentAccount();
    return signOut;
  };

  return {
    authUser,
    isLoggedIn,
    initials,
    initializeAuthUser,
    clearSession,
    setUser,
    credentialToLinkOnLogin,
    setCredentialToLinkOnLogin,
  };
});
